import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Pagination, Row, Col, Badge, Alert,
} from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import ProtectedSection from '../components/ProtectedSection';
import zelleService from '../services/zelleService';
import dailyRateService from '../services/dailyRateService';
import ZelleModal from '../components/ZelleModal';
import ZellePayModal from '../components/ZellePayModal';
import ZelleViewModal from '../components/ZelleViewModal';
import ReceiptPrintButton from '../components/ReceiptPrintButton';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

const badgeVariant = (status) => ({ PENDING: 'warning', CONFIRMED: 'info', NOT_CONFIRMED: 'secondary', PAID: 'success' }[status] || 'dark');
const isDeletable = (status) => ['PENDING', 'NOT_CONFIRMED', 'CONFIRMED'].includes(status);
const isEditable = (status) => ['PENDING', 'CONFIRMED'].includes(status);

export default function ZellePage() {
  const { hasRight } = useContext(AuthContext);
  const canView = hasRight('ZELLE_VIEW');
  const canUpdate = hasRight('ZELLE_UPDATE');
  const canDelete = hasRight('ZELLE_DELETE');
  const canConfirm = hasRight('ZELLE_CONFIRM');
  const canPay = hasRight('ZELLE_PAY');

  const initialDates = useMemo(() => defaultDateRange(), []);
  const [rows, setRows] = useState([]);
  const [rate, setRate] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentOperatorFilter, setPaymentOperatorFilter] = useState('');
  const [paymentOperators, setPaymentOperators] = useState([]);
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [payShow, setPayShow] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [viewShow, setViewShow] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fetchRate = useCallback(() => { dailyRateService.get().then(setRate).catch(() => setRate(null)); }, []);

  const fetchAll = useCallback(() => {
    if (!canView) return;
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) {
      setErrorMsg(dateError); setRows([]); setTotal(0); return;
    }
    setErrorMsg('');
    zelleService.list({
      page,
      pageSize,
      search: filter,
      status: statusFilter,
      paidByUserId: paymentOperatorFilter || undefined,
      dateFrom,
      dateTo,
    })
      .then((r) => {
        setRows(r?.data || []);
        setTotal(Number(r?.total || 0));
        const pages = Math.max(1, Number(r?.totalPages || Math.ceil(Number(r?.total || 0) / pageSize)));
        if (page > pages) setPage(pages);
      })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur chargement Zelle.'));
  }, [canView, dateFrom, dateTo, filter, page, pageSize, paymentOperatorFilter, statusFilter]);

  const fetchPaymentOperators = useCallback(() => {
    if (!canView) return;
    zelleService.paymentOperators()
      .then((data) => setPaymentOperators(Array.isArray(data) ? data : []))
      .catch(() => setPaymentOperators([]));
  }, [canView]);

  useEffect(() => { fetchRate(); }, [fetchRate]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchPaymentOperators(); }, [fetchPaymentOperators]);
  useEffect(() => { setPage(1); }, [filter, statusFilter, paymentOperatorFilter, dateFrom, dateTo, pageSize]);

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const openCreate = () => { setEditRow(null); setShowModal(true); };
  const openEdit = (r) => { setEditRow(r); setShowModal(true); };
  const openView = (r) => {
    if (!r?.id) return;
    setViewShow(true); setViewRow(null); setViewError(''); setViewLoading(true);
    zelleService.get(r.id).then(setViewRow)
      .catch((e) => setViewError(e?.response?.data?.message || 'Erreur chargement détails.'))
      .finally(() => setViewLoading(false));
  };
  const closeView = () => { setViewShow(false); setViewRow(null); setViewError(''); setViewLoading(false); };
  const save = (payload) => {
    setErrorMsg('');
    const op = editRow?.id ? zelleService.update(editRow.id, payload) : zelleService.create(payload);
    op.then(() => { setShowModal(false); setEditRow(null); fetchRate(); fetchAll(); fetchPaymentOperators(); })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur enregistrement Zelle.'));
  };
  const decide = (id, decision) => {
    if (!window.confirm(`Confirmer l'action : ${decision} ?`)) return;
    setErrorMsg('');
    zelleService.decision(id, decision).then(fetchAll)
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur décision.'));
  };
  const openPay = (r) => { setPayRow(r); setPayShow(true); };
  const doPay = (payload) => {
    if (!payRow?.id) return;
    setErrorMsg('');
    zelleService.pay(payRow.id, payload).then(() => {
      setPayShow(false); setPayRow(null); fetchAll(); fetchPaymentOperators();
    }).catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur paiement.'));
  };
  const remove = (r) => {
    if (!window.confirm('Supprimer cette transaction Zelle ? Cette action est irréversible.')) return;
    setErrorMsg('');
    zelleService.remove(r.id).then(() => { fetchAll(); fetchPaymentOperators(); })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur suppression.'));
  };

  const buildReceiptDocument = async (r) => {
    const full = await zelleService.get(r.id);
    const grossUSD = Number(full?.amountUSD || 0);
    const feeUSD = Number(full?.feeUSD || 0);
    const netUSD = Math.max(0, grossUSD - feeUSD);
    const paidBy = full?.paidByUser?.username || full?.createdByUser?.username || '—';
    return {
      title: `Reçu Zelle${full?.confirmationCode ? ` - ${full.confirmationCode}` : ''}`,
      rows: [
        { label: 'Sender', value: full?.senderName || '—' },
        { label: 'Receiver', value: full?.beneficiaryName || '—' },
        { label: 'Code Zelle', value: full?.confirmationCode || '—' },
        { label: 'Motif', value: full?.transactionReason || '—' },
        { label: 'Statut', value: full?.status || '—' },
        { label: 'Montant reçu brut', value: full?.amountUSD == null ? '—' : `${grossUSD.toFixed(2)} USD` },
        { label: 'Frais', value: `${feeUSD.toFixed(2)} USD` },
        { label: 'Net à payer', value: full?.amountUSD == null ? '—' : `${netUSD.toFixed(2)} USD` },
        { label: 'Équivalent HTG', value: full?.amountHTG == null ? '—' : `${Number(full.amountHTG).toFixed(2)} HTG` },
        { label: 'Taux utilisé', value: Number(full?.rateUsdHtg || 0) > 0 ? `${Number(full.rateUsdHtg).toFixed(2)} HTG` : '—' },
        { label: 'Payé USD', value: `${Number(full?.paidUSD || 0).toFixed(2)} USD` },
        { label: 'Payé HTG', value: `${Number(full?.paidHTG || 0).toFixed(2)} HTG` },
        { label: 'Paiement traité par', value: full?.paidByUser?.username || '—' },
        { label: 'Créée le', value: full?.createdAt ? new Date(full.createdAt).toLocaleString() : '—' },
        { label: 'Note transaction', value: full?.note || '—' },
        { label: 'Note paiement', value: full?.paymentNote || '—' },
      ],
      signatures: { client: 'Signature client', cashier: paidBy },
    };
  };

  const rateNum = Number(rate?.usdToHtg || 0);

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>Zelle</h2>
        <ProtectedSection code="ZELLE_CREATE"><Button onClick={openCreate}>+ Nouvelle Zelle</Button></ProtectedSection>
      </div>
      <Alert variant="secondary" className="mb-3"><strong>Taux du jour :</strong> {rateNum > 0 ? `1 USD = ${rateNum.toFixed(2)} HTG` : 'Non défini (allez dans “Taux du jour”)'}</Alert>
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      <Row className="g-2 mb-3 align-items-end">
        <Col md={3}><Form.Label>Date début</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Col>
        <Col md={3}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Col>
        <Col md={6}><Form.Label>Recherche</Form.Label><InputGroup><Form.Control placeholder="Code, sender, receiver, client, téléphone, utilisateur..." value={filter} onChange={(e) => setFilter(e.target.value)} /><Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button></InputGroup></Col>
        <Col md={4}>
          <Form.Label>Statut</Form.Label>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="">Tous statuts</option><option value="PENDING">Pending</option><option value="CONFIRMED">Confirmé</option><option value="NOT_CONFIRMED">Non confirmé</option><option value="PAID">Payé</option></Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Paiement traité par</Form.Label>
          <Form.Select value={paymentOperatorFilter} onChange={(e) => setPaymentOperatorFilter(e.target.value)}>
            <option value="">Tous les utilisateurs</option>
            {paymentOperators.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Affichage</Form.Label>
          <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 lignes</option><option value={25}>25 lignes</option><option value={50}>50 lignes</option><option value={100}>100 lignes</option></Form.Select>
        </Col>
      </Row>

      <Table hover responsive>
        <thead><tr><th>Date</th><th>Code</th><th>Sender</th><th>Receiver</th><th className="text-end">Frais USD</th><th className="text-end">Net USD</th><th className="text-end">Taux</th><th className="text-end">HTG</th><th>Paiement traité par</th><th>Statut</th><th style={{ width: 440 }}>Actions</th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const financialReady = Number(r.amountUSD || 0) > 0 && Number(r.rateUsdHtg || 0) > 0;
            return <tr key={r.id}>
              <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td><td>{r.confirmationCode}</td>
              <td><div className="fw-semibold">{r.senderName || '—'}</div></td>
              <td><div className="fw-semibold">{r.beneficiaryName || '—'}</div><div className="text-muted small">{r.client ? `Client : ${r.client.firstName} ${r.client.lastName}` : 'Non-client / non renseigné'}</div></td>
              <td className="text-end">{r.amountUSD == null ? '—' : Number(r.feeUSD || 0).toFixed(2)}</td>
              <td className="text-end">{r.amountUSD == null ? '—' : Math.max(0, Number(r.amountUSD) - Number(r.feeUSD || 0)).toFixed(2)}</td>
              <td className="text-end">{Number(r.rateUsdHtg || 0) > 0 ? Number(r.rateUsdHtg).toFixed(2) : '—'}</td>
              <td className="text-end">{r.amountHTG == null ? '—' : Number(r.amountHTG).toFixed(2)}</td>
              <td>{r.paidByUser?.username || '—'}</td>
              <td><Badge bg={badgeVariant(r.status)}>{r.status}</Badge></td>
              <td>
                {canView && <Button size="sm" variant="outline-primary" className="me-2 mb-1" onClick={() => openView(r)}>Voir</Button>}
                {r.status === 'PAID' && <ReceiptPrintButton className="me-2 mb-1" getDocument={() => buildReceiptDocument(r)} />}
                {canUpdate && isEditable(r.status) && <Button size="sm" variant="outline-secondary" className="me-2 mb-1" onClick={() => openEdit(r)}>Modifier</Button>}
                {r.status === 'PENDING' && canConfirm && <><Button size="sm" variant="outline-success" className="me-2 mb-1" onClick={() => decide(r.id, 'CONFIRMED')}>Confirmer</Button><Button size="sm" variant="outline-warning" className="me-2 mb-1" onClick={() => decide(r.id, 'NOT_CONFIRMED')}>Refuser</Button></>}
                {r.status === 'CONFIRMED' && canPay && <Button size="sm" variant={financialReady ? 'success' : 'outline-success'} className="me-2 mb-1" onClick={() => openPay(r)} title={!financialReady ? 'Complétez le montant avant le paiement' : ''}>Payer</Button>}
                {canDelete && <Button size="sm" variant="outline-danger" className="mb-1" disabled={!isDeletable(r.status)} title={!isDeletable(r.status) ? 'Une Zelle payée ne peut pas être supprimée' : ''} onClick={() => remove(r)}>Supprimer</Button>}
              </td>
            </tr>;
          })}
          {rows.length === 0 && <tr><td colSpan={11} className="text-center text-muted">Aucune transaction Zelle.</td></tr>}
        </tbody>
      </Table>

      <Row className="mt-2"><Col md={4}><span className="text-muted">Résultats : {total} • Page {page} / {totalPages}</span></Col><Col md={8} className="d-flex justify-content-md-end"><Pagination size="sm" className="mb-0">{paginationItems}</Pagination></Col></Row>

      <ZelleModal show={showModal} onHide={() => { setShowModal(false); setEditRow(null); }} onSave={save} initialData={editRow} currentRate={rateNum} />
      <ZellePayModal show={payShow} onHide={() => { setPayShow(false); setPayRow(null); }} onPay={doPay} zelle={payRow} />
      <ZelleViewModal show={viewShow} onHide={closeView} zelle={viewRow} loading={viewLoading} error={viewError} />
    </Container>
  );
}
